import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { ROLES } from "../../game/roles";
import { ROLE_ICONS, ROLE_COLORS } from "./roleIcons";
import { Avatar } from "./Avatar";
import { SHEET_FADE, SHEET_SLIDE } from "./motionPresets";

const SheetHeader = ({ character, onClose }) => (
  <div className="flex items-center gap-3 mb-5">
    <Avatar name={character.name} size={46} />
    <div className="min-w-0 flex-1">
      <p className="label-mono text-[#C64BB4]">Assegna un ruolo</p>
      <p className="font-display text-xl text-[#14233D] truncate" data-testid="role-sheet-character">
        {character.name}
      </p>
    </div>
    <button
      type="button"
      onClick={onClose}
      data-testid="role-sheet-close"
      className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-[#7433C1]"
    >
      <X size={20} />
    </button>
  </div>
);

const RoleButton = ({ role, natural, onAssign }) => {
  const Icon = ROLE_ICONS[role.key];
  const color = ROLE_COLORS[role.key];
  return (
    <button
      type="button"
      data-testid={`assign-role-${role.key}`}
      data-natural={natural ? "true" : "false"}
      onClick={() => onAssign(role.key)}
      style={natural ? { borderColor: `${color}B3`, boxShadow: `0 2px 0 ${color}8C` } : undefined}
      className={`role-btn w-full min-h-[56px] rounded-[20px] px-3 py-2.5 flex items-center gap-3 text-left ${
        natural ? "!bg-white" : "!bg-[#F6F3FD]"
      }`}
    >
      <span
        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
        style={{ background: natural ? `${color}D9` : `${color}55` }}
      >
        <Icon size={16} className="text-[#14233D]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-[#14233D]">{role.label}</span>
        <span className="role-hint block text-[#6E6694] line-clamp-2 mt-0.5" data-testid={`role-hint-${role.key}`}>
          {role.description}
        </span>
      </span>
    </button>
  );
};

export const RoleSheet = ({ character, assignments, onAssign, onClose }) => {
  const freeRoles = useMemo(() => ROLES.filter((role) => !assignments[role.key]), [assignments]);
  return (
    <AnimatePresence>
      {character && (
        <motion.div className="fixed inset-0 z-40 flex items-end justify-center" {...SHEET_FADE}>
          <button
            type="button"
            aria-label="Chiudi"
            data-testid="role-sheet-backdrop"
            className="absolute inset-0 bg-[#EAF7FF]/70 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            data-testid="role-sheet"
            className="sheet relative w-full max-w-md rounded-t-[32px] px-5 pt-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
            {...SHEET_SLIDE}
          >
            <SheetHeader character={character} onClose={onClose} />
            <p className="text-xs text-[#506780] mb-3 leading-snug" data-testid="role-sheet-explainer">
              I ruoli evidenziati sono quelli più adatti al personaggio. Ma la ciurma è tua: puoi schierarlo dove vuoi.
            </p>
            <div className="flex flex-col gap-2 max-h-[62dvh] overflow-y-auto pb-1" data-testid="role-sheet-list">
              {freeRoles.map((role) => (
                <RoleButton
                  key={role.key}
                  role={role}
                  natural={character.natural.includes(role.key)}
                  onAssign={onAssign}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
